import React, {useEffect, useState} from "react";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Snackbar, Stack, TextField, Tooltip, Typography
} from "@mui/material";
import SummarizeIcon from "@mui/icons-material/Summarize";

import {
  fnAlertPrep, rewriteVersOreNoteWithCopilotApi, useCreateVersOre2,
  useGetAppUserProgettiDTO, validationSchemaVersOre2
} from "./Services";
import {VersOre2, VersOre2AIFields} from "./types";
import {FormField} from "../../common/FormField";
import {
  useFieldsVersOre2_1, useFieldsVersOre2_2, useFieldsVersOre2_3,
  useFieldsVersOre2_5
} from "./FormVersOre2Voice";
import ReportProg from "../reports/ReportProg";
import {AIFormAssistant} from "../../common/ai/AIFormAssistant";
import {clampAIText, loadAICopilotConfig} from "../../common/ai/AITextAssistant";
import {selectAIField} from "../../common/ai/AIFieldSelection";

type VersOre2Form = Yup.InferType<typeof validationSchemaVersOre2>;

type PendingSelection = {
  originalText: string;
  projectId: string | null;
};

interface AddVersOre2VoiceProps {
  open: boolean;
  onClose: () => void;
  defaultDate?: Date | null;
}

const AddVersOre2Voice: React.FC<AddVersOre2VoiceProps> = ({open, onClose, defaultDate}) => {
  // 1. Stato della form e dell'assistente AI
  const {control, handleSubmit, watch, setValue, reset} = useForm<VersOre2Form>({
    resolver: yupResolver(validationSchemaVersOre2),
    defaultValues: {
      idprog: "", idnodo: "", idatt: "",
      datareg: defaultDate ?? new Date(), datains: new Date(),
      luogo: "SEDE", orestraord: 0, minstraord: 0
    }
  });

  const [indclibloc, setIndclibloc] = useState(0);
  const [openAlert, setOpenAlert] = useState(false);
  const [numAlertPrep, setNumAlertPrep] = useState(0);
  const [openReport, setOpenReport] = useState(false);
  const [aiMessage, setAIMessage] = useState("");
  const [aiWarnings, setAIWarnings] = useState<string[]>([]);
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);

  const isBlocked = indclibloc === 2;
  const watchIdProg = watch("idprog");
  const watchLuogo = watch("luogo");
  const watchOreStraord = watch("orestraord");
  const watchMinStraord = watch("minstraord");

  // 2. Dati e dropdown: le query rimangono nei relativi hook
  const keycoddip = localStorage.getItem("keycoddip") || "";
  const {data: appUserProgDTO} = useGetAppUserProgettiDTO(watchIdProg, keycoddip);
  const molt = parseFloat(String(appUserProgDTO?.results?.[0]?.molt).replace(",", ".")) || 1;

  const f1 = useFieldsVersOre2_1(false, isBlocked);
  const {fields: f2, ready: dependentFieldsReady} = useFieldsVersOre2_2(
    watchIdProg, setValue, setIndclibloc, isBlocked
  );
  const f3 = useFieldsVersOre2_3(isBlocked);
  const f5 = useFieldsVersOre2_5(isBlocked);
  const {mutateAsync, isPending} = useCreateVersOre2();

  // 3. Reset, blocco cliente, alert progetto e calcolo ore
  useEffect(() => {
    if (!open) return;
    reset({
      idprog: "", idnodo: "", idatt: "",
      datareg: defaultDate ?? new Date(), datains: new Date(),
      luogo: "SEDE", orestraord: 0, minstraord: 0
    });
    setAIMessage("");
    setAIWarnings([]);
    setPendingSelection(null);
    setIndclibloc(0);
  }, [open, defaultDate, reset]);

  useEffect(() => {
    if (indclibloc === 2) setOpenAlert(true);
  }, [indclibloc]);

  useEffect(() => {
    if (!watchIdProg) {
      setNumAlertPrep(0);
      return;
    }
    let active = true;
    fnAlertPrep(watchIdProg)
      .then(response => {
        if (active) setNumAlertPrep(Number(response?.numalertprep) || 0);
      })
      .catch(() => {
        if (active) setNumAlertPrep(0);
      });
    return () => { active = false; };
  }, [watchIdProg]);

  useEffect(() => {
    const total = ((Number(watchOreStraord) || 0) * 60 +
      (Number(watchMinStraord) || 0)) * molt;
    let ore = Math.ceil(total / 60);
    if (watchLuogo === "TRASFERTA") ore = ore <= 4 ? 4 : 8;
    setValue("oreord", ore);
  }, [watchOreStraord, watchMinStraord, watchLuogo, molt, setValue]);

  // 4. Matching locale: prima il progetto, poi nodo e attivita del progetto.
  useEffect(() => {
    if (!open || !pendingSelection || isBlocked) return;

    const {originalText, projectId} = pendingSelection;

    if (!projectId) {
      const projectField = f2.find(field => field.name === "idprog");
      const projects = typeof projectField?.options === "function"
        ? projectField.options()
        : projectField?.options;
    //
    //  console.log("TESTO ORIGINALE:", pendingSelection.originalText);
    //  console.log( "NUMERO PROGETTI:",  Array.isArray(projects) ? projects.length : 0);
    //
    // console.table(
    //                 Array.isArray(projects)
    //                   ? projects.map((p: any) => ({
    //                       idprog: p.idprog,
    //                       desprogblocco: p.desprogblocco
    //                     }))
    //                   : []
    //               );



      if (!Array.isArray(projects) || projects.length === 0) return;



      const matchedId = selectAIField({
        fields: f2,
        fieldName: "idprog",
        searchText: originalText,
        currentValue: watchIdProg,
        setValue
      });

      if (!matchedId) {
        setAIWarnings(["Progetto non riconosciuto: selezionalo manualmente."]);
        setAIMessage("Interpretazione completata; progetto da verificare.");
        setPendingSelection(null);
        return;
      }

      setPendingSelection({originalText, projectId: matchedId});
      return;
    }

    // I nodi e le attivita dipendono dal progetto, non l'uno dall'altra.
    if (projectId !== watchIdProg || !dependentFieldsReady) return;

    const warnings: string[] = [];
    const node = selectAIField({
      fields: f2,
      fieldName: "idnodo",
      searchText: originalText,
      setValue
    });
    if (!node) warnings.push("Nodo non riconosciuto: selezionalo manualmente.");

    const activity = selectAIField({
      fields: f2,
      fieldName: "idatt",
      searchText: originalText,
      setValue
    });
    if (!activity) warnings.push("Attivita non riconosciuta: selezionala manualmente.");

    setAIWarnings(warnings);
    setAIMessage("Compilazione completata. Verifica i dati prima del salvataggio.");
    setPendingSelection(null);
  }, [open, pendingSelection, dependentFieldsReady, watchIdProg, isBlocked, f2, setValue]);

  // 5. L'AI compila solo i campi semplici: le dropdown usano il testo originale.
  const handleAIApply = (fields: VersOre2AIFields, originalText: string) => {
    if (isBlocked) return;
    setAIWarnings([]);

    if (fields.datareg) {
      setValue("datareg", new Date(`${fields.datareg}T12:00:00`), {shouldValidate: true});
    }
    if (fields.orestraord != null) {
      setValue("orestraord", fields.orestraord, {shouldValidate: true});
    }
    if (fields.minstraord != null) {
      setValue("minstraord", fields.minstraord, {shouldValidate: true});
    }
    if (fields.luogo) setValue("luogo", fields.luogo, {shouldValidate: true});
    if (fields.note) setValue("note", fields.note, {shouldValidate: true});

    // Non eredita le selezioni del rapportino precedente.
    const projectField = f2.find(field => field.name === "idprog");
    projectField?.onChange?.(null, "");
    setValue("idprog", "", {shouldValidate: true});
    setPendingSelection({originalText, projectId: null});
    setAIMessage("Dati interpretati. Ricerca locale dei campi in corso...");
  };

  // 6. Salvataggio: mantiene il payload del gestionale e il limite delle note.
  const onSubmit: SubmitHandler<VersOre2Form> = async data => {
    if (isBlocked) return;
    const config = await loadAICopilotConfig();
    const codprog = data.idprog?.split("-")[1] ?? "";
    const codnodo = data.idnodo?.split("-")[2] ?? "";
    const codatt = data.idatt?.split("-")[2] ?? "";
    const ditta = Number(data.idprog?.split("-")[0]) || 0;
    const coddip = localStorage.getItem("keycoddip") || "";

    const payload = {
      ...data,
      indlavoraz: 0, indora: 0, indtipovers: 2,
      valutadqr: "EURO", umtempodqr: 2, indtipocosdqr: 0,
      costodqr: 30, impdqr: 90, protocollo: 1037, minord: 0,
      ditta, codprog, codnodo, codatt, coddip,
      note: clampAIText(data.note || "", config.noteMaxLength).value
    };

    await mutateAsync(payload as VersOre2);
    onClose();
  };

  // 7. Interfaccia: assistente AI, campi del rapportino e dialog di supporto.
  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Nuovo Time Report - AI</Typography>
            {watchIdProg && (
              <Tooltip title="Report Nodi Progetto">
                <IconButton color="primary" onClick={() => setOpenReport(true)}>
                  <SummarizeIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{mt: 1}}>
            {/* Assistente: testo, dettatura, Copilot e compilazione AI */}
            <Box sx={{p: 2, border: 1, borderColor: "divider", borderRadius: 2}}>
              <AIFormAssistant<VersOre2AIFields>
                formName="versore2"
                onApply={handleAIApply}
                disabled={isBlocked}
                rewriteText={({text, modeId, prompt, maxLength}) =>
                  rewriteVersOreNoteWithCopilotApi({
                    inputText: text, rewriteType: modeId,
                    rewritePrompt: prompt, maxChars: maxLength
                  })
                }
              />
              {aiMessage && <Alert severity="success" sx={{mt: 2}}>{aiMessage}</Alert>}
              {aiWarnings.map((warning, index) => (
                <Alert key={index} severity="warning" sx={{mt: 1}}>{warning}</Alert>
              ))}
            </Box>

            {/* Campi del rapportino */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                  {f1.map(f => <FormField key={String(f.name)} control={control} field={f} />)}
                </Stack>
                <Stack spacing={1}>
                  {f2.map(f => <FormField key={String(f.name)} control={control} field={f} />)}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {f3.map(f => <FormField key={String(f.name)} control={control} field={f} />)}
                </Stack>
                <Controller
                  name="note"
                  control={control}
                  render={({field, fieldState}) => (
                    <TextField
                      id="versore2-ai-note" label="Note" multiline rows={4} fullWidth
                      value={String(field.value || "")}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      disabled={isBlocked}
                    />
                  )}
                />

                <Stack direction="row" spacing={1}>
                  {f5.map(f => <FormField key={String(f.name)} control={control} field={f} />)}
                </Stack>
                <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{mt: 2}}>
                  <Button onClick={onClose} color="error">Annulla</Button>
                  <Button type="submit" variant="contained" disabled={isPending || isBlocked}>
                    {isPending ? "Salvando..." : "Salva"}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Report dei nodi del progetto */}
      <Dialog open={openReport} onClose={() => setOpenReport(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report Nodi Progetto</DialogTitle>
        <DialogContent><ReportProg key={watchIdProg} idprogprop={watchIdProg} /></DialogContent>
        <DialogActions><Button onClick={() => setOpenReport(false)}>Chiudi</Button></DialogActions>
      </Dialog>

      {/* Avvisi del gestionale */}
      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{vertical: "top", horizontal: "center"}}
      >
        <Alert severity="error" variant="filled">
          Cliente sospeso: inserimento non consentito
        </Alert>
      </Snackbar>
      <Snackbar
        open={numAlertPrep < 0}
        autoHideDuration={3000}
        anchorOrigin={{vertical: "top", horizontal: "center"}}
      >
        <Alert severity="error" variant="filled">
          Alert ORE PREP: verificare monitor progetti e avvertire commerciale.
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddVersOre2Voice;
